import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { 
  createTask, 
  getProjectTasks, 
  updateTask, 
  deleteTask
} from '../functions/api_fethcers';

export const useProjectTasks = (projectId, filters = {}, autoFetch = true) => {
  const queryClient = useQueryClient();
  const tasksQueryKey = ['tasks', projectId, filters];

  // Query for fetching all tasks for a project
  const {
    data: tasks = [],
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(
    tasksQueryKey,
    () => getProjectTasks(projectId, filters),
    {
      enabled: autoFetch && !!projectId, // Only fetch if projectId is provided and autoFetch is true
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false
    }
  );

  // Mutation for creating a new task
  const { 
    mutateAsync: createTaskMutation,
    isLoading: isCreating
  } = useMutation(
    (taskData) => createTask({ projectId, taskData }),
    {
      onSuccess: () => {
        // Invalidate and refetch tasks query
        queryClient.invalidateQueries(tasksQueryKey);
        toast.success('Task created successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to create task');
      }
    }
  );

  // Mutation for updating a task
  const { 
    mutateAsync: updateTaskMutation,
    isLoading: isUpdating
  } = useMutation(
    ({ taskId, taskData }) => updateTask({ projectId, taskId, taskData }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(tasksQueryKey);
        toast.success('Task updated successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to update task');
      }
    }
  );

  // Mutation for deleting a task
  const { 
    mutateAsync: deleteTaskMutation,
    isLoading: isDeleting
  } = useMutation(
    (taskId) => deleteTask(projectId, taskId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(tasksQueryKey);
        toast.success('Task deleted successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete task');
      }
    }
  );

  // Mutation for updating task status - uses regular updateTask instead of specialzed updateTaskStatus
  const {
    mutateAsync: updateTaskStatusMutation,
    isLoading: isUpdatingStatus
  } = useMutation(
    ({ taskId, status }) => updateTask({ 
      projectId, 
      taskId, 
      taskData: { status } 
    }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(tasksQueryKey);
        // Special message if moving from completed/cancelled back to active
        if (variables.status === 'to_do') {
          toast.success('Task reactivated');
        } else {
          const statusMessage = {
            'to_do': 'Task moved to To Do',
            'in_progress': 'Task moved to In Progress',
            'completed': 'Task marked as Completed',
            'blocked': 'Task marked as Blocked',
            'cancelled': 'Task cancelled'
          };
          toast.success(statusMessage[variables.status] || 'Task status updated');
        }
      },
      // Enable optimistic updates
      onMutate: async ({ taskId, status }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(tasksQueryKey);

        // Snapshot the previous value
        const previousTasks = queryClient.getQueryData(tasksQueryKey);

        // Optimistically update to the new value
        queryClient.setQueryData(tasksQueryKey, old => {
          return old.map(task =>
            task.id === taskId ? { ...task, status } : task
          );
        });

        // Return a context object with the snapshot
        return { previousTasks };
      },
      // If the mutation fails, roll back using the context from onMutate
      // AND toast so the failure is visible instead of silently reverting.
      onError: (err, variables, context) => {
        if (context?.previousTasks) {
          queryClient.setQueryData(tasksQueryKey, context.previousTasks);
        }
        toast.error(err.message || 'Failed to update task status');
      }
    }
  );

  // Task statistics
  const taskStats = {
    total: tasks.length,
    todo: tasks.filter(task => task.status === 'to_do').length,
    inProgress: tasks.filter(task => task.status === 'in_progress').length,
    done: tasks.filter(task => task.status === 'done').length
  };

  // Return everything needed for task management
  return {
    tasks,
    taskStats,
    isLoading,
    isError,
    error,
    refetch,
    createTask: createTaskMutation,
    updateTask: updateTaskMutation,
    deleteTask: deleteTaskMutation,
    updateTaskStatus: updateTaskStatusMutation,
    isCreating,
    isUpdating,
    isDeleting,
    isUpdatingStatus
  };
};